'use client';

import React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { buildSearchBox, SearchEngine } from '@coveo/headless';
import { Stack, Typography } from '@unity/cloud-ui';
import { initializeClientEngine } from '@/lib/coveo/engine';
import { BaseSearchInput } from '@/components/search/base-search-input';
import { ThemeContext } from '@/components/theme';
import themeConstants from '@/components/theme/theme-constants';

export type NotFoundPageProps = {
  params: Promise<{
    locale: string;
  }>;
};

export function NotFoundPage() {
  const locale = useLocale();
  const t = useTranslations();
  const { zoom } = React.useContext(ThemeContext);
  const searchFieldRef = React.useRef<HTMLInputElement>(null);
  const [searchEngine, setSearchEngine] = React.useState<SearchEngine | null>(null);

  const searchBox = React.useMemo(
    () =>
      searchEngine
        ? buildSearchBox(searchEngine, {
            options: { numberOfSuggestions: 10 },
          })
        : null,
    [searchEngine],
  );

  const [searchState, setSearchState] = React.useState(searchBox?.state);

  React.useEffect(() => {
    initializeClientEngine(locale).then((engine) => {
      setSearchEngine(engine);
    });
  }, [locale]);

  React.useEffect(() => {
    const unsubscribe = searchBox?.subscribe(() => {
      setSearchState(searchBox?.state);
    });
    return unsubscribe;
  }, [searchBox]);

  return (
    <Stack
      alignContent="center"
      direction="column"
      alignItems="center"
      px={4}
      pt={12}
      mx="auto"
      sx={{
        zoom: themeConstants.zoom[zoom],
      }}
    >
      <Typography variant="h1" textAlign="center" gutterBottom>
        {t('page:not-found:title')}
      </Typography>
      <Typography variant="body1" textAlign="center">
        {t('page:not-found:description')}
      </Typography>

      <Stack width="100%" direction="row" gap={2} padding={4}>
        <BaseSearchInput
          id="search-input"
          navigateOnSearch
          renderSearchButton
          fullWidth
          size="extraLarge"
          searchBoxState={searchState}
          searchFieldRef={searchFieldRef}
          methods={
            searchBox
              ? {
                  ...searchBox,
                  updateText: (text: string) => {
                    searchBox?.updateText(text);
                    return;
                  },
                }
              : null
          }
        />
      </Stack>
    </Stack>
  );
}
